import { forwardRef, ReactNode } from "react";

import {
  Dialog as MuiDialog,
  DialogTitle as MuiDialogTitle,
  DialogActions as MuiDialogActions,
  Box,
  ThemeProvider,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import { StorybookConfirmationDialogPreview } from "./StorybookConfirmationDialogPreview";

import { FormButton } from "@/forms-design-system/FormButton";
import { FormIcon } from "@/forms-design-system/FormIcon/FormIcon";
import { FormTypography } from "@/forms-design-system/FormTypography";
import { InvalidThemeErrorMessage } from "@/forms-design-system/InvalidThemeErrorMessage";
import { useDynamicFormV2Theme } from "@/forms-design-system/Tokens/dynamicFormV2Theme";
import { Cross, Success } from "@/icons/outlined";

type FormConfirmationDialogLayoutProps = {
  slots: {
    icon: ReactNode;
    closeButton: ReactNode;
    title: ReactNode;
    description: ReactNode;
    actions: ReactNode[];
  };
};

export const FormConfirmationDialogLayout = ({ slots }: FormConfirmationDialogLayoutProps) => {
  const theme = useTheme();

  return (
    <>
      <MuiDialogTitle
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          gap: 2,
          padding: theme.spacing(3, 3, 1, 3),
        }}
      >
        <Box
          sx={{
            padding: 0.75,
            color: theme.palette.formsComponents.dialog.iconColor,
            backgroundColor: theme.palette.formsComponents.dialog.iconBackgroundColor,
            borderRadius: theme.radius(1),
          }}
        >
          {slots.icon}
        </Box>
        {slots.closeButton}
      </MuiDialogTitle>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          padding: theme.spacing(1, 3, 2, 3),
          overflowY: "auto",
          scrollbarColor: `${theme.palette.formsComponents.global.scrollbarThumbColor} transparent`,
          ["&::-webkit-scrollbar-track"]: {
            background: "transparent",
          },
          ["&::-webkit-scrollbar-thumb"]: {
            background: theme.palette.formsComponents.global.scrollbarThumbColor,
          },
        }}
      >
        <FormTypography variant="h6" color={theme.palette.formsComponents.dialog.titleColor}>
          {slots.title}
        </FormTypography>
        <FormTypography variant="body2" color={theme.palette.formsComponents.dialog.textColor}>
          {slots.description}
        </FormTypography>
      </Box>
      <MuiDialogActions
        disableSpacing
        sx={{
          flexDirection: { xs: "column", md: "row-reverse" },
          alignItems: { xs: "stretch", md: "center" },
          justifyContent: { xs: "stretch", md: "flex-start" },
          gap: 1,
          padding: theme.spacing(2, 3),
        }}
      >
        {slots.actions}
      </MuiDialogActions>
    </>
  );
};

export type InnerFormConfirmationDialogProps = {
  title: ReactNode;
  description: ReactNode;
  actions: ReactNode[];
  open?: boolean;
  onClose?: () => void;
  inline?: boolean;
};

export const InnerFormConfirmationDialog = forwardRef<HTMLDivElement, InnerFormConfirmationDialogProps>(
  ({ inline, open = false, title, description, actions, ...rest }, ref) => {
    const theme = useTheme();

    if (!theme.palette.forms || !theme.palette.formsComponents?.dialog) {
      return <InvalidThemeErrorMessage />;
    }

    const content = (
      <FormConfirmationDialogLayout
        slots={{
          icon: (
            <FormIcon size="large">
              <Success />
            </FormIcon>
          ),
          closeButton: (
            <FormButton
              variant="ghost"
              startIcon={<Cross />}
              onClick={rest.onClose}
              iconButton
              sx={{ flexShrink: 0 }}
            />
          ),
          title,
          description,
          actions,
        }}
      />
    );

    if (inline) {
      return (
        <StorybookConfirmationDialogPreview ref={ref} {...rest}>
          {content}
        </StorybookConfirmationDialogPreview>
      );
    }

    return (
      <MuiDialog ref={ref} {...rest} open={open} maxWidth="xs" sx={{ ["& .MuiPaper-root"]: { overflowY: "visible" } }}>
        {content}
      </MuiDialog>
    );
  }
);

InnerFormConfirmationDialog.displayName = "InnerFormConfirmationDialog";

export const FormConfirmationDialog = forwardRef<HTMLDivElement, InnerFormConfirmationDialogProps>((props, ref) => {
  const theme = useTheme();

  if (!theme.palette.forms || !theme.palette.metadata) {
    return <InvalidThemeErrorMessage />;
  }

  // This hook is rendered conditionally, but that should never happen unless in we're in dev mode
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const dialogTheme = useDynamicFormV2Theme(theme.palette.metadata.baseColor, "light");

  return (
    <ThemeProvider theme={dialogTheme}>
      <InnerFormConfirmationDialog ref={ref} {...props} />
    </ThemeProvider>
  );
});

FormConfirmationDialog.displayName = "FormConfirmationDialog";
